import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';

import { Organization, useOrganization } from '@/models/organization';
import { getOrganizations, UserFromGetSession } from '@/models/user';
import { useLocalStorageState } from '@/services/localStorage';

export const ORGANIZATION_LOCAL_STORAGE_KEY = 'current-organization';

export const useCurrentOrganizationFromSession = (
  currentSession?: UserFromGetSession | null
): [Organization | null, (organization: { slug: string } | null, redirectTo?: string) => void] => {
  const [slug, setSlug] = useLocalStorageState<string | null>(ORGANIZATION_LOCAL_STORAGE_KEY, null);

  const setCurrentOrganization = useCallback(
    (organization: { slug: string } | null, redirectTo = '/') => {
      console.log(organization);

      if (!organization) {
        setSlug(null);
      } else {
        setSlug(organization.slug);
      }

      // Reload the app once organization is changed, this makes sure we have fresh data
      // eslint-disable-next-line react-compiler/react-compiler
      window.location.href = redirectTo;
    },
    [setSlug]
  );

  return useMemo(() => {
    if (!currentSession) {
      return [null, setCurrentOrganization];
    }

    if (slug) {
      const currentOrganization = getOrganizations(currentSession)?.find(
        (organization) => organization.slug === slug
      );

      if (currentOrganization) {
        return [currentOrganization, setCurrentOrganization];
      }
    }

    const defaultOrganization = getOrganizations(currentSession)?.[0] ?? null;

    if (slug !== defaultOrganization?.slug) {
      setSlug(defaultOrganization?.slug ?? null);
    }

    return [defaultOrganization, setCurrentOrganization];
  }, [slug, setSlug, currentSession, setCurrentOrganization]);
};

export const useCurrentOrganizationFromUrl = () => {
  const { organization: slug } = useParams();
  const { data: organization } = useOrganization(slug);

  return { organization: organization ?? null };
};
